/**
 * RESPONSIBILITY: hold the current flag values for THIS browser.
 *
 * localStorage only. No server, no database, no session — anyone can open
 * /secret-flags and flip a switch; it affects their browser and nobody else's.
 *
 *   const showBar = useFlag(FLAG_DEV_BAR);
 */
import { useSyncExternalStore } from 'react';
import { allFlags, flagDefaults, isKnownFlag } from './registry';
import { encodeFlags, decodeFlags } from './flag-code';

const STORAGE_KEY = 'acfi.flags';

/* ---- store --------------------------------------------------------------- */

const listeners = new Set<() => void>();

/**
 * Computed on first READ, never at module load — and that is the whole point.
 *
 * read() drops any stored key that isKnownFlag() rejects, so it is only correct once
 * every registerFlag() has run. Evaluated at module load it was not: this module is
 * exported from the react barrel BEFORE ./flags/built-in, so the registry was still
 * empty, every saved flag was discarded as unknown, and the snapshot was {}.
 *
 * The symptom was flags that worked until you reloaded. Worse, setFlag() then wrote
 * `{...snapshot, [key]: enabled}` over the top, so the next toggle persisted the loss
 * — one flag survived and the rest were erased from localStorage for good.
 *
 * Deferring it also makes the fix hold for flags an APPLICATION registers, which
 * importing built-in from here would not have: those register later still.
 */
let snapshot: Record<string, boolean> | null = null;

const current = (): Record<string, boolean> => (snapshot ??= read());

function read(): Record<string, boolean> {
  const defaults = flagDefaults();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaults;
    const stored = JSON.parse(raw) as Record<string, boolean>;
    // Unknown keys are dropped: a flag that no longer exists shouldn't linger forever.
    for (const [k, v] of Object.entries(stored)) {
      if (isKnownFlag(k)) defaults[k] = Boolean(v);
    }
    return defaults;
  } catch {
    return defaults;
  }
}

function publish(): void {
  snapshot = read();
  listeners.forEach((l) => l());
}

export function setFlag(key: string, enabled: boolean): void {
  if (!isKnownFlag(key)) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current(), [key]: enabled }));
  publish();
}

export function resetFlags(): void {
  localStorage.removeItem(STORAGE_KEY);
  publish();
}

// Keep other tabs of the same browser in step.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => { if (e.key === STORAGE_KEY) publish(); });
}

/* ---- hooks --------------------------------------------------------------- */

export const useFlags = (): Record<string, boolean> =>
  useSyncExternalStore(
    (cb) => { listeners.add(cb); return () => listeners.delete(cb); },
    // current() memoises, so these return the SAME object between publishes —
    // useSyncExternalStore requires that, and would loop forever otherwise.
    current,
    current,
  );

export const useFlag = (key: string): boolean => Boolean(useFlags()[key]);

/** Non-hook read, for code outside React (the batching loader). */
export const flagEnabled = (key: string): boolean => Boolean(current()[key]);

/** Definitions joined to current values, for the /secret-flags screen. */
export function useFlagRows() {
  const values = useFlags();
  return allFlags().map((f) => ({ ...f, enabled: Boolean(values[f.key]) }));
}

/* ---- flag code ----------------------------------------------------------- */

/** The enabled flags as a short string, e.g. "AD". */
export const getFlagCode = (): string => encodeFlags(current());

export const useFlagCode = (): string => encodeFlags(useFlags());

/**
 * ENABLE-ONLY. Every character switches its flag ON. There is no way to express
 * "turn this off", so flags a code does not mention stay exactly as this browser
 * already has them — which is what makes a shared link safe to click.
 *
 * Returns the keys it actually changed.
 */
export function enableFlagCode(code: string): string[] {
  const { keys } = decodeFlags(code);
  const turnedOn: string[] = [];
  const next = { ...current() };

  for (const key of keys) {
    if (next[key] === true) continue;
    next[key] = true;
    turnedOn.push(key);
  }

  if (turnedOn.length === 0) return [];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  publish();
  return turnedOn;
}
